import { useMutation } from '@tanstack/react-query';
import { ApiResponse } from 'apisauce';
import LangEnum from '../../../../../lang/LangEnum';
import { TeceoApiResponse } from '../../../../../lib/basicRepository';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { createLinkToDownloadBlob } from '../../../../../utils/createLinkToDownload';
import orderActionsRepository from '../../../repositories/orderActions.repository';
import { exportOrderPdf } from '../../../repositories/ordersRepository/onlineOrdersRepository';
import { exportOrdersCsvWithOffline } from '../utils/exportOrdersCsvWithOffline';
import { ExportOrdersCsvParams, ExportOrdersPdfParams } from './exportOrder.types';
import { mapCsvScope } from './exportOrderScope';

export const useExportOrdersPdfMutation = (isSyncScope: boolean, onClose: () => void) =>
  useMutation<
    TeceoApiResponse<Blob | { ok: true; message?: string }>,
    unknown,
    ExportOrdersPdfParams
  >({
    mutationFn: (params) => exportOrderPdf({ ...params, isSyncScope }),
    onSuccess: (response, params) => {
      if (isSyncScope && response.data instanceof Blob) {
        createLinkToDownloadBlob({
          data: response.data,
          filename: params.filename,
          archiveType: 'pdf',
        });
      } else {
        snackbarStore.handleMessage({
          type: 'info',
          message: params.translate(LangEnum.ORDER_EXPORT_ORDER_PDF_SUCCESS_BATCH_SNACKBAR),
        });
      }
      onClose();
    },
    onError: (_error, params) => {
      snackbarStore.handleMessage({
        type: 'error',
        message: params.translate(LangEnum.GLOBAL_EXPORT_ERROR),
      });
    },
  });

export const useExportOrdersCsvMutation = (isOfflineMode: boolean, onClose: () => void) =>
  useMutation<ApiResponse<Blob, Blob> | void, unknown, ExportOrdersCsvParams>({
    networkMode: 'always',
    mutationFn: async ({
      filename,
      exportScope,
      brandId,
      translate,
      selectedTab,
      filterMaxResults,
    }) => {
      const { scope } = exportScope;
      const exportByIds = scope.type === 'ids';
      const isFromMassActions = !!selectedTab;

      if (exportByIds && scope.ids.length === 1 && !isFromMassActions) {
        return await orderActionsRepository({
          orderId: scope.ids[0],
          translate,
          brandId,
          isOfflineMode,
        }).exportCsv(filename);
      }

      return await exportOrdersCsvWithOffline({
        paramsToExport: { searchParams: mapCsvScope(exportScope.scope), translate, isOfflineMode },
        selectedIds: exportByIds ? scope.ids : undefined,
        selectedTab,
        filterMaxResults,
      });
    },
    onSuccess: (response, { translate }) => {
      if (!response) {
        onClose();
        return;
      }

      if (!response.ok && response.status === 400) {
        snackbarStore.handleMessage({
          type: 'error',
          message: translate(LangEnum.ORDER_EXPORT_CSV_ERROR_MESSAGE),
        });
        return;
      }
      onClose();
    },
    onError: (_error, { translate }) => {
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.GLOBAL_EXPORT_ERROR),
      });
    },
  });
